import { Waveform } from "@rosc/ui/v32/recorder-bar";
import { Pause, Play } from "lucide-react";
import type { JSX } from "react";
import { memo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Visualizer, type VisualizerProps } from "react-sound-visualizer";

interface RecorderVisualizerProps {
  audio: MediaStream | null;
  paused: boolean;
  controls?: boolean;
  onToggle?: () => void;
  mode?: "continuous" | "current";
  // Fixed levels (0–1) for stories and screenshots; no microphone.
  preview?: number[];
}

function SafeVisualizer(props: VisualizerProps): JSX.Element {
  const stopRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    return (): void => {
      stopRef.current?.();
      stopRef.current = undefined;
    };
  }, []);

  return (
    <Visualizer {...props}>
      {({ canvasRef, stop }) => {
        if (stopRef.current && stopRef.current !== stop) {
          stopRef.current();
        }
        stopRef.current = stop;

        return props.children ? (
          typeof props.children === "function" ? (
            props.children({ canvasRef, stop })
          ) : (
            props.children
          )
        ) : (
          <canvas ref={canvasRef} />
        );
      }}
    </Visualizer>
  );
}

const RecorderVisualizer = ({
  audio,
  paused,
  controls = false,
  onToggle,
  mode = "continuous",
  preview,
}: RecorderVisualizerProps): JSX.Element => {
  const { t } = useTranslation();

  // Live recording: the v3.2 waveform (as on iOS), bars driven by the mic
  // level; frozen while paused.
  if (!controls) {
    return (
      <div
        className="flex h-[52px] min-h-[52px] w-full items-center rounded-xl bg-v3-surface-2 px-4 shadow-v3-hairline"
        data-paused={paused}
        data-testid="recorder-waveform"
      >
        <Waveform
          label={t("components.recorderInner.waveform")}
          paused={paused}
          levels={preview}
          stream={audio && audio.getAudioTracks().length > 0 ? audio : null}
        />
      </div>
    );
  }

  return (
    <div className="w-full h-[52px] min-h-[52px] bg-primary rounded-xl flex items-center">
      <div className="w-1/5 flex justify-center items-center">
        <button
          type="button"
          className="bg-transparent text-white px-2 py-1 rounded-sm"
          onClick={onToggle}
        >
          {paused ? <Play fill="#fff" /> : <Pause fill="#fff" />}
        </button>
      </div>
      <div className="w-3/5 h-1/2 flex justify-center items-center">
        {audio && audio.getAudioTracks().length > 0 ? (
          <SafeVisualizer
            key={`${paused}-${mode}-${!!audio}`}
            autoStart
            audio={audio}
            barRadius={0.1}
            heightNorm={0.2}
            mode={mode}
            rectWidth={"thick"}
            slices={50}
            strokeColor={"#ffffff"}
          >
            {({ canvasRef }) => (
              <canvas ref={canvasRef} className="w-full h-full rounded-xl" />
            )}
          </SafeVisualizer>
        ) : (
          <div className="w-3/5 h-1/2" />
        )}
      </div>
    </div>
  );
};

export default memo(RecorderVisualizer);
